import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";
import {
  FiAlertTriangle,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiEdit3,
  FiExternalLink,
  FiMoreVertical,
  FiPauseCircle,
  FiPlayCircle,
  FiPlusCircle,
  FiRefreshCw,
  FiTrendingUp,
} from "react-icons/fi";

import { CmtsEnterpriseModal } from "@components/CmtsEnterpriseModal.js";
import { CmtsEnterpriseForm } from "@components/forms/index.js";
import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
  type CmtsGridFilterControl,
} from "@components/data-grid/index.js";
import type { CmtsSlaVm } from "@cmts/mappers/slaMappers.js";
import { requireCmtsUiAction } from "@cmts/utils/cmtsActionGuards.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

type SlaRow = Record<string, unknown>;
type CmtsFormOption = { readonly value: string; readonly label: string };
type ActionTone = "blue" | "slate" | "amber" | "green" | "indigo" | "rose";

function text(row: SlaRow, keys: readonly string[], fallback = ""): string {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return fallback;
}

function normalizeCode(value: unknown): string {
  return String(value ?? "").trim().replace(/[\s-]+/g, "_").toUpperCase();
}

function displayCode(value: unknown, fallback = "-"): string {
  const normalized = normalizeCode(value);
  if (!normalized) return fallback;
  return normalized
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function ownerLabel(row: SlaRow): string {
  return text(
    row,
    ["ownerName", "ownerDisplayName", "ownerFullName", "owner", "assigneeName", "assignee"],
    "-",
  );
}

function priorityCode(row: SlaRow): string {
  return normalizeCode(row.priorityCode ?? row.priorityName ?? row.priority);
}

function statusCode(row: SlaRow): string {
  if (row.paused === true || row.isPaused === true) return "PAUSED";
  if (row.breach === true || row.isBreached === true) return "BREACHED";
  if (row.nearBreach === true || row.isNearBreach === true) return "NEAR_BREACH";
  return normalizeCode(
    row.slaStatus ?? row.status ?? row.slaStateCode ?? row.slaStateName ?? "WITHIN_SLA",
  );
}

function isPaused(row: SlaRow): boolean {
  return statusCode(row) === "PAUSED";
}

function isBreached(row: SlaRow): boolean {
  return ["BREACHED", "BREACH"].includes(statusCode(row));
}

function isNearBreach(row: SlaRow): boolean {
  return ["NEAR_BREACH", "AT_RISK"].includes(statusCode(row));
}

function dateLabel(value: unknown): string {
  if (!value) return "-";
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return String(value).split("T")[0] ?? "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
}

function toDateTimeLocal(value: unknown): string {
  if (!value) return "";
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return "";
  const offset = parsed.getTimezoneOffset() * 60_000;
  return new Date(parsed.getTime() - offset).toISOString().slice(0, 16);
}

function buildSlaFields(
  complaintOptions: readonly CmtsFormOption[],
  slaRuleOptions: readonly CmtsFormOption[],
) {
  return [
    {
      name: "complaintId",
      label: "Complaint",
      type: "select" as const,
      required: true,
      options: complaintOptions,
      placeholder: "Select complaint",
      help: "Priority and owner are inherited from the selected complaint.",
    },
    {
      name: "slaRuleId",
      label: "SLA Profile / Rule",
      type: "select" as const,
      required: true,
      options: slaRuleOptions,
      placeholder: "Select SLA profile and rule",
      help: "The selected rule controls the SLA due-date calculation.",
    },
    { name: "startDate", label: "Start date/time", type: "datetime-local" as const, required: true },
    { name: "responseDueAt", label: "Response due", type: "datetime-local" as const },
    { name: "containmentDueAt", label: "Containment due", type: "datetime-local" as const },
    { name: "targetDate", label: "Resolution due", type: "datetime-local" as const, required: true },
    { name: "verificationDueAt", label: "Verification due", type: "datetime-local" as const },
    {
      name: "remarks",
      label: "Remarks",
      type: "textarea" as const,
      placeholder: "Add a reason, dependency or operational note",
    },
  ] as const;
}

function defaultSlaValues(row?: SlaRow | null): Record<string, unknown> {
  if (row) {
    return {
      ...row,
      complaintId: text(row, ["complaintId", "linkedEntityId"]),
      slaRuleId: text(row, ["slaRuleId", "ruleId"]),
      startDate: toDateTimeLocal(row.startDate ?? row.startAtUtc ?? row.createdAt),
      responseDueAt: toDateTimeLocal(row.responseDueAt ?? row.responseDueAtUtc),
      containmentDueAt: toDateTimeLocal(row.containmentDueAt ?? row.containmentDueAtUtc),
      targetDate: toDateTimeLocal(
        row.targetDate ?? row.resolutionDueAt ?? row.resolutionDueAtUtc ?? row.dueDate,
      ),
      verificationDueAt: toDateTimeLocal(row.verificationDueAt ?? row.verificationDueAtUtc),
    };
  }
  const now = new Date();
  const target = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  return {
    startDate: toDateTimeLocal(now.toISOString()),
    targetDate: toDateTimeLocal(target.toISOString()),
  };
}

function initialGridStateFromUrl(params: URLSearchParams): {
  filters: Record<string, string>;
  searchText: string;
} {
  const filters: Record<string, string> = {};
  const searchText = params.get("q") ?? params.get("search") ?? "";
  const aliases: Record<string, string> = {
    owner: "ownerName",
    assignee: "ownerName",
    statusGroup: "slaStatus",
    status: "slaStatus",
  };
  params.forEach((value, key) => {
    if (!["q", "search"].includes(key) && value) {
      filters[aliases[key] ?? key] = decodeURIComponent(value)
        .replace(/[_-]+/g, " ")
        .trim()
        .toLowerCase();
    }
  });
  return { filters, searchText };
}

function SlaActionCard(props: {
  readonly icon: ReactNode;
  readonly tone: ActionTone;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean | undefined;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`cmts-sla-action-card cmts-sla-action-card--${props.tone}`}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <span className="cmts-sla-action-card__icon">{props.icon}</span>
      <span className="cmts-sla-action-card__copy">
        <strong>{props.title}</strong>
        <small>{props.description}</small>
      </span>
      <span className="cmts-sla-action-card__arrow">›</span>
    </button>
  );
}

export function CmtsSlaPage(props: {
  readonly sla?: CmtsSlaVm | null | undefined;
  readonly timelineEvents?: readonly SlaRow[] | undefined;
  readonly slaRows?: readonly SlaRow[] | undefined;
  readonly diagnostics?: readonly ComponentDiagnostic[] | undefined;
  readonly onOpenComplaint?: ((row: SlaRow) => void | Promise<void>) | undefined;
  readonly onEscalate?: ((row: SlaRow) => void | Promise<void>) | undefined;
  readonly onCreateTracker?: ((payload: SlaRow) => void | Promise<void>) | undefined;
  readonly onUpdateTracker?: ((row: SlaRow, payload: SlaRow) => void | Promise<void>) | undefined;
  readonly onPauseSla?: ((row: SlaRow) => void | Promise<void>) | undefined;
  readonly onResumeSla?: ((row: SlaRow) => void | Promise<void>) | undefined;
  readonly onRecalculateSla?: ((row: SlaRow) => void | Promise<void>) | undefined;
  readonly masterOptions?: {
    readonly complaint?: readonly CmtsFormOption[] | undefined;
    readonly slaType?: readonly CmtsFormOption[] | undefined;
  } | undefined;
}) {
  const diagnostics = props.diagnostics ?? [];
  const rows = props.slaRows ?? props.timelineEvents ?? [];
  const [searchParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<SlaRow | null>(null);
  const [actionsFor, setActionsFor] = useState<SlaRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialGridState = useMemo(() => initialGridStateFromUrl(searchParams), [searchParams]);
  const summary = useMemo(
    () => ({
      total: rows.length,
      within: rows.filter((row) => !isPaused(row) && !isBreached(row) && !isNearBreach(row)).length,
      near: rows.filter(isNearBreach).length,
      breached: rows.filter(isBreached).length,
      paused: rows.filter(isPaused).length,
    }),
    [rows],
  );
  const ownerOptions = useMemo(
    () =>
      Array.from(new Set(rows.map(ownerLabel).filter((name) => name && name !== "-"))).map(
        (name) => ({ value: name, label: name }),
      ),
    [rows],
  );
  const slaFields = useMemo(
    () => buildSlaFields(props.masterOptions?.complaint ?? [], props.masterOptions?.slaType ?? []),
    [props.masterOptions?.complaint, props.masterOptions?.slaType],
  );

  const columns = useMemo<readonly CmtsGridColumn<SlaRow>[]>(
    () => [
      {
        id: "title",
        header: "SLA item",
        sortable: true,
        filterable: true,
        minWidth: 250,
        render: (row) => (
          <span className="cmts-grid-title-cell">
            <strong>{text(row, ["title"], "SLA Tracker")}</strong>
            <small>{text(row, ["profileName", "profileCode"], "Governed SLA")}</small>
          </span>
        ),
      },
      {
        id: "slaStatus",
        header: "SLA",
        sortable: true,
        filterable: true,
        minWidth: 130,
        render: (row) => {
          const code = statusCode(row);
          return (
            <span className={`cmts-sla-state-chip cmts-sla-state-chip--${code.toLowerCase()}`}>
              {displayCode(code, "Within SLA")}
            </span>
          );
        },
      },
      {
        id: "priority",
        header: "Priority",
        sortable: true,
        filterable: true,
        minWidth: 115,
        render: (row) => {
          const code = priorityCode(row);
          return code ? (
            <span className={`cmts-priority-chip cmts-priority-chip--${code.toLowerCase()}`}>
              {displayCode(code)}
            </span>
          ) : <span className="cmts-cell-muted">-</span>;
        },
      },
      {
        id: "ownerName",
        header: "Owner",
        sortable: true,
        filterable: true,
        minWidth: 170,
        render: (row) => <span>{ownerLabel(row)}</span>,
      },
      {
        id: "dueDate",
        header: "Resolution due",
        sortable: true,
        filterable: true,
        minWidth: 175,
        render: (row) => <span>{dateLabel(row.dueDate ?? row.resolutionDueAt ?? row.targetDate)}</span>,
      },
      {
        id: "complaintNumber",
        header: "Complaint",
        sortable: true,
        filterable: true,
        minWidth: 165,
        render: (row) => (
          <strong className="cmts-sla-complaint-code">
            {text(row, ["complaintNumber", "complaintCode", "complaintReference"], "-")}
          </strong>
        ),
      },
      {
        id: "slaActions",
        header: "Actions",
        sortable: false,
        filterable: false,
        minWidth: 125,
        render: (row) => (
          <button
            type="button"
            className="cmts-notification-actions-button"
            aria-label="Open SLA actions"
            onClick={(event) => {
              event.stopPropagation();
              setError(null);
              setActionsFor(row);
            }}
          >
            <span className="cmts-notification-actions-button__icon"><FiMoreVertical /></span>
            <span>Actions</span>
          </button>
        ),
      },
    ],
    [],
  );

  const filters = useMemo<readonly CmtsGridFilterControl<SlaRow>[]>(
    () => [
      {
        id: "slaStatus",
        label: "SLA",
        columnId: "slaStatus",
        operator: "equals",
        placeholder: "All SLA states",
        options: [
          { value: "within_sla", label: "Within SLA" },
          { value: "near_breach", label: "Near breach" },
          { value: "breached", label: "Breached" },
          { value: "paused", label: "Paused" },
        ],
      },
      {
        id: "priority",
        label: "Priority",
        columnId: "priority",
        operator: "equals",
        placeholder: "All priorities",
        options: [
          { value: "low", label: "Low" },
          { value: "medium", label: "Medium" },
          { value: "high", label: "High" },
          { value: "critical", label: "Critical" },
        ],
      },
      {
        id: "ownerName",
        label: "Owner",
        columnId: "ownerName",
        operator: "equals",
        placeholder: "All owners",
        options: ownerOptions,
      },
    ],
    [ownerOptions],
  );

  const runAction = async (
    handler: ((row: SlaRow) => void | Promise<void>) | undefined,
    handlerName: string,
    row: SlaRow,
    fallback: string,
  ) => {
    try {
      setBusy(true);
      setError(null);
      await requireCmtsUiAction(handler, handlerName)(row);
      setActionsFor(null);
    } catch (caught) {
      setError(normalizeUserSafeApiError(caught).userMessage || fallback);
    } finally {
      setBusy(false);
    }
  };

  const closeActions = () => {
    if (!busy) {
      setActionsFor(null);
      setError(null);
    }
  };
  const closeForm = () => {
    if (!busy) {
      setFormOpen(false);
      setSelected(null);
      setError(null);
    }
  };
  const openCreate = () => {
    setSelected(null);
    setError(null);
    setFormOpen(true);
  };
  const openEdit = (row: SlaRow) => {
    setSelected(row);
    setActionsFor(null);
    setError(null);
    setFormOpen(true);
  };

  return (
    <PageContainer title="Service Level Management">
      {diagnostics.length > 0 ? <RuntimeDiagnosticBanner diagnostics={diagnostics} /> : null}

      <section className="cmts-sla-page-header">
        <div>
          <span className="cmts-sla-page-header__eyebrow">SLA governance</span>
          <h2>Operational SLA tracker</h2>
          <p>Monitor response, containment, resolution and verification commitments from one workspace.</p>
        </div>
        <Button className="cmts-sla-create-button" onClick={openCreate} disabled={busy}>
          <FiPlusCircle /> Create SLA
        </Button>
      </section>

      <section className="cmts-sla-summary-grid" aria-label="SLA summary">
        <article className="cmts-sla-summary-card cmts-sla-summary-card--total">
          <span className="cmts-sla-summary-card__icon"><FiCalendar /></span>
          <div><small>Total trackers</small><strong>{summary.total}</strong></div>
        </article>
        <article className="cmts-sla-summary-card cmts-sla-summary-card--within">
          <span className="cmts-sla-summary-card__icon"><FiCheckCircle /></span>
          <div><small>Within SLA</small><strong>{summary.within}</strong></div>
        </article>
        <article className="cmts-sla-summary-card cmts-sla-summary-card--near">
          <span className="cmts-sla-summary-card__icon"><FiClock /></span>
          <div><small>Near breach</small><strong>{summary.near}</strong></div>
        </article>
        <article className="cmts-sla-summary-card cmts-sla-summary-card--breached">
          <span className="cmts-sla-summary-card__icon"><FiAlertTriangle /></span>
          <div><small>Breached</small><strong>{summary.breached}</strong></div>
        </article>
        <article className="cmts-sla-summary-card cmts-sla-summary-card--paused">
          <span className="cmts-sla-summary-card__icon"><FiPauseCircle /></span>
          <div><small>Paused</small><strong>{summary.paused}</strong></div>
        </article>
      </section>

      <CmtsSmartDataGrid<SlaRow>
        title="SLA tracker queue"
        subtitle="Review commitments, ownership, priority and linked complaints."
        rows={rows}
        columns={columns}
        filters={filters}
        initialFilterValues={initialGridState.filters}
        initialSearchText={initialGridState.searchText}
        rowKey="id"
        defaultPageSize={10}
        filterSummary={[
          "Tenant/persona scope applied",
          "Backend-calculated SLA dates",
          "Governed actions with audit events",
        ]}
        onRowClick={props.onOpenComplaint}
        emptyMessage="No SLA tracker rows are available for this persona."
      />

      {actionsFor ? (
        <CmtsEnterpriseModal
          open={true}
          title="SLA actions"
          subtitle={text(actionsFor, ["title"], "SLA tracker")}
          size="md"
          onClose={closeActions}
        >
          <div className="cmts-sla-actions-modal">
            {error ? <div className="cmts-sla-inline-error">{error}</div> : null}
            <section className="cmts-sla-action-summary">
              <div><small>Current state</small><strong>{displayCode(statusCode(actionsFor))}</strong></div>
              <div><small>Priority</small><strong>{displayCode(priorityCode(actionsFor))}</strong></div>
              <div><small>Owner</small><strong>{ownerLabel(actionsFor)}</strong></div>
              <div><small>Resolution due</small><strong>{dateLabel(actionsFor.resolutionDueAt ?? actionsFor.dueDate)}</strong></div>
            </section>
            <div className="cmts-sla-actions-grid">
              <SlaActionCard
                icon={<FiExternalLink />}
                tone="blue"
                title="Open complaint"
                description="Open the linked complaint workspace."
                disabled={busy || !props.onOpenComplaint || !text(actionsFor, ["complaintId", "linkedEntityId"])}
                onClick={() => void runAction(props.onOpenComplaint, "onOpenComplaint", actionsFor, "Unable to open complaint.")}
              />
              <SlaActionCard
                icon={<FiEdit3 />}
                tone="slate"
                title="Edit SLA"
                description="Update the selected SLA dates and rule."
                disabled={busy || !props.onUpdateTracker}
                onClick={() => openEdit(actionsFor)}
              />
              <SlaActionCard
                icon={<FiPauseCircle />}
                tone="amber"
                title="Pause SLA"
                description="Pause the SLA clock with an audit event."
                disabled={busy || !props.onPauseSla || isPaused(actionsFor)}
                onClick={() => void runAction(props.onPauseSla, "onPauseSla", actionsFor, "Unable to pause SLA.")}
              />
              <SlaActionCard
                icon={<FiPlayCircle />}
                tone="green"
                title="Resume SLA"
                description="Resume a currently paused SLA clock."
                disabled={busy || !props.onResumeSla || !isPaused(actionsFor)}
                onClick={() => void runAction(props.onResumeSla, "onResumeSla", actionsFor, "Unable to resume SLA.")}
              />
              <SlaActionCard
                icon={<FiRefreshCw />}
                tone="indigo"
                title="Recalculate"
                description="Re-evaluate all backend SLA timers."
                disabled={busy || !props.onRecalculateSla}
                onClick={() => void runAction(props.onRecalculateSla, "onRecalculateSla", actionsFor, "Unable to recalculate SLA.")}
              />
              <SlaActionCard
                icon={<FiTrendingUp />}
                tone="rose"
                title="Escalate"
                description="Create a governed escalation for this SLA."
                disabled={busy || !props.onEscalate}
                onClick={() => void runAction(props.onEscalate, "onEscalate", actionsFor, "Unable to escalate SLA.")}
              />
            </div>
          </div>
        </CmtsEnterpriseModal>
      ) : null}

      <CmtsEnterpriseModal
        open={formOpen}
        title={selected ? "Edit SLA tracker" : "Create SLA tracker"}
        subtitle="Configure a complaint-linked SLA using a governed profile and due dates."
        size="lg"
        onClose={closeForm}
      >
        <div className="cmts-sla-form-shell">
          <section className="cmts-sla-form-intro">
            <span className="cmts-sla-form-intro__icon"><FiCalendar /></span>
            <div>
              <strong>{selected ? "Update SLA commitment" : "Create SLA commitment"}</strong>
              <p>Complaint priority and owner remain complaint-authoritative and appear in the tracker automatically.</p>
            </div>
          </section>
          <CmtsEnterpriseForm
            fields={slaFields}
            initialValues={defaultSlaValues(selected)}
            submitLabel={selected ? "Save SLA" : "Create SLA"}
            busy={busy}
            error={error}
            onCancel={closeForm}
            onSubmit={async (payload) => {
              try {
                setBusy(true);
                setError(null);
                if (selected) {
                  await requireCmtsUiAction(props.onUpdateTracker, "onUpdateTracker")(selected, payload);
                } else {
                  await requireCmtsUiAction(props.onCreateTracker, "onCreateTracker")(payload);
                }
                setFormOpen(false);
                setSelected(null);
              } catch (caught) {
                setError(normalizeUserSafeApiError(caught).userMessage || "SLA tracker save failed.");
              } finally {
                setBusy(false);
              }
            }}
          />
        </div>
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}